import React, { useState, useEffect, useRef } from 'react'
import ImageSlide from './ImageSlide'
import VideoSlide from './VideoSlide'
import FallbackLogo from '../FallbackLogo'
import './mediaCarousel.css'

const IMAGE_DISPLAY_DURATION = 5000 // 5 seconds

export default function MediaCarousel({ content }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadErrors, setLoadErrors] = useState(new Set())
  const timerRef = useRef(null)

  // Reset carousel when content changes (e.g., branch office change)
  useEffect(() => {
    setCurrentIndex(0)
    setLoadErrors(new Set())
  }, [content])

  // Filter out items with load errors
  const validContent = content.filter(item => !loadErrors.has(item.Id))

  // If all content failed to load, show fallback logo
  if (validContent.length === 0) {
    return <FallbackLogo />
  }

  const currentItem = validContent[currentIndex]

  // Handle media load errors
  const handleMediaError = () => {
    console.error('Failed to load media:', currentItem?.MediaUrl)
    setLoadErrors(prev => new Set([...prev, currentItem.Id]))
    // Advance to next item immediately
    advanceToNext()
  }

  // Advance to next item in carousel
  const advanceToNext = () => {
    setCurrentIndex(prev => (prev + 1) % validContent.length)
  }

  // Auto-advance for images
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    // Only set timer for images (videos use onEnded event)
    if (currentItem?.MediaType === 'image') {
      timerRef.current = setTimeout(() => {
        advanceToNext()
      }, IMAGE_DISPLAY_DURATION)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [currentIndex, validContent.length, currentItem])

  // Preload next item for smooth transitions
  useEffect(() => {
    if (validContent.length === 0) return

    const nextIndex = (currentIndex + 1) % validContent.length
    const nextItem = validContent[nextIndex]

    if (!nextItem) return

    if (nextItem.MediaType === 'image') {
      const img = new Image()
      img.src = nextItem.MediaUrl
    } else if (nextItem.MediaType === 'video') {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.src = nextItem.MediaUrl
    }
  }, [currentIndex, validContent])

  return (
    <div className="media-carousel" data-testid="media-carousel">
      {validContent.map((item, index) => {
        const isActive = index === currentIndex

        if (item.MediaType === 'image') {
          return (
            <ImageSlide
              key={item.Id}
              src={item.MediaUrl}
              alt={item.Title || 'Screensaver image'}
              isActive={isActive}
              onError={handleMediaError}
            />
          )
        }

        if (item.MediaType === 'video') {
          return (
            <VideoSlide
              key={item.Id}
              src={item.MediaUrl}
              isActive={isActive}
              onEnded={advanceToNext}
              onError={handleMediaError}
            />
          )
        }

        return null
      })}
    </div>
  )
}
